import { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'tabler-icons-react';
import { Portal } from 'react-portal';
import { Button } from 'Elements';
import cx from 'classnames';
import styles from 'partials/modal-box.module.scss';

export const ModalBox = ({
	children,
	onClose,
	onDestroy,
	isOpen,
	isPersist,
	size,
	className
}) => {
	const [isOpenState, setIsOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const wrapperRef = useRef(null);

	const onCloseClick = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleClickOutside = useCallback(
		(e) => {
			if (
				wrapperRef &&
				!wrapperRef.current.contains(e.target) &&
				!isPersist
			) {
				e.stopPropagation();
				onCloseClick();
			}
		},
		[isPersist, onCloseClick]
	);

	const handleKeydown = useCallback(
		(e) => {
			if (e.keyCode === 27 && isOpenState) {
				onCloseClick();
			}
		},
		[isOpenState, onCloseClick]
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeydown);
		const animationTime = 280; // milisecond

		if (!isOpenState && isOpen) {
			setIsOpen(true);
		} else if (isOpenState && !isOpen && !isClosing) {
			setIsClosing(true);
			setTimeout(() => {
				setIsOpen(false);
				setIsClosing(false);
			}, animationTime);
		}
		return () => {
			onDestroy?.();
			document.removeEventListener('keydown', handleKeydown);
		};
	}, [isOpenState, isOpen, handleKeydown, isClosing, onDestroy]);

	const contentClassName = cx(styles.content, styles.fadein, {
		[styles.contentOpen]: !isClosing,
		[styles.contentClose]: isClosing
	});

	const wrapperClassName = cx(styles[`modal_${size}`], styles.wrapper, {
		[styles.wrapperOpen]: !isClosing,
		[styles.wrapperClose]: isClosing
	});

	if (!isOpenState) {
		return null;
	}

	return (
		<Portal closeOnEsc node={document.getElementById('modal-box')}>
			<div
				className={contentClassName}
				role="button"
				tabIndex="0"
				onKeyPress={handleClickOutside}
				onClick={handleClickOutside}>
				{!isPersist && (
					<Button
						aria-label="close modal"
						className={styles.close}
						onClick={onCloseClick}>
						<X />
					</Button>
				)}
				<div className={wrapperClassName} ref={wrapperRef}>
					<div className={cx(styles.modal, className)}>
						{children}
					</div>
				</div>
			</div>
		</Portal>
	);
};

ModalBox.defaultProps = {
	size: 'sm'
};
